/**
 * URL ↔ View bridge.
 *
 * Pairs the reducer-backed NavigationContext with `history.pushState` so
 * sidebar navigation updates the address bar, F5 lands on the current view,
 * and browser back/forward traverse the navigation stack.
 *
 * Scope: top-level views only. Drill-down views with selection params
 * (obra-details et al.) keep their parent-view URL for now; full param-aware
 * routing is a separate RFC.
 *
 * Maintenance: `VIEW_TO_PATH` uses `Record<View, string>` so adding a new view
 * literal without registering its path is a TypeScript error.
 */
import {
  OVERLAY_DRAWER_KINDS,
  SESSION_DRAWER_KINDS,
  type OverlayDrawerKind,
  type OverlayDrawerMode,
  type OverlayDrawerState,
  type View,
} from './navigation-state';

// Derived from the union's source-of-truth array so adding a kind there
// automatically makes it URL-restorable. Phases 1-11 covered.
const KNOWN_DRAWER_KINDS: ReadonlySet<OverlayDrawerKind> = new Set(OVERLAY_DRAWER_KINDS);

function isDrawerKind(value: string): value is OverlayDrawerKind {
  return KNOWN_DRAWER_KINDS.has(value as OverlayDrawerKind);
}

/**
 * Encode the drawer stack as a single `?drawer=...` query value, comma-
 * separated, top-of-stack last. Two segment shapes:
 *
 * - **Entity drawers** — `kind:id[:mode]`. The id addresses a specific record.
 * - **Session drawers** — `kind[::mode]` (no id). The entity is implicit in
 *   the auth/session context (e.g. `meu_perfil` is always the logged-in
 *   user). Edit mode keeps the empty id segment so the parser stays uniform.
 *
 * Returns an empty string when the stack is empty (caller should drop the param).
 */
export function stackToQuery(stack: readonly OverlayDrawerState[]): string {
  if (stack.length === 0) return '';
  return stack
    .map((entry) => {
      const isSession = SESSION_DRAWER_KINDS.has(entry.kind);
      const head = isSession ? entry.kind : `${entry.kind}:${entry.id}`;
      if (entry.mode !== 'edit') return head;
      return isSession ? `${entry.kind}::edit` : `${head}:edit`;
    })
    .join(',');
}

/**
 * Parse a query string back into a drawer stack. Accepts both entity shape
 * (`kind:id[:mode]`) and session shape (`kind[::mode]`). Unknown kinds and
 * malformed entries are dropped silently so a broken URL never crashes the
 * app — it just opens fewer drawers.
 *
 * Examples:
 * - `projeto:abc` → entity, view mode
 * - `atividade:def:edit` → entity, edit mode
 * - `meu_perfil` → session, view mode
 * - `meu_perfil::edit` → session, edit mode
 */
export function queryToStack(search: string): OverlayDrawerState[] {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const raw = params.get('drawer');
  if (!raw) return [];

  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part): OverlayDrawerState | null => {
      const [kind, idSegment, mode] = part.split(':');
      if (!kind || !isDrawerKind(kind)) return null;
      const isSession = SESSION_DRAWER_KINDS.has(kind);
      // Entity drawers require a non-empty id; session drawers reject any id.
      const id = idSegment ?? '';
      if (isSession && id !== '') return null;
      if (!isSession && id === '') return null;
      const resolvedMode: OverlayDrawerMode = mode === 'edit' ? 'edit' : 'view';
      return { kind, id, mode: resolvedMode };
    })
    .filter((entry): entry is OverlayDrawerState => entry !== null);
}

/**
 * Build the full URL (path + optional drawer query) for a given view+stack.
 * Colons and commas inside `?drawer=` are intentionally kept literal — both
 * are URI-safe sub-delimiters per RFC 3986 and the URL stays readable.
 */
export function buildUrl(view: View, stack: readonly OverlayDrawerState[]): string {
  const path = viewToPath(view);
  const query = stackToQuery(stack);
  return query ? `${path}?drawer=${query}` : path;
}

const VIEW_TO_PATH: Record<View, string> = {
  'home': '/',
  // Obras
  'obras': '/obras',
  'obras-dashboard': '/obras/dashboard',
  'obras-jornadas': '/obras/jornadas',
  'obras-areas': '/obras/areas',
  'obras-ambientes': '/obras/ambientes',
  'obras-pontos': '/obras/pontos',
  'obras-produtos': '/obras/produtos',
  'obras-cabos': '/obras/cabos',
  'obras-tubos': '/obras/tubos',
  'obras-pessoas': '/obras/envolvidos',
  'obras-servicos': '/obras/servicos',
  'obras-vistorias': '/obras/vistorias',
  'obras-andamentos': '/obras/etapas',
  'obras-cronograma': '/obras/cronograma',
  // OS
  'os-ativas': '/os',
  'os-agenda': '/os/agenda',
  // Comercial
  'oportunidades-ativas-v2': '/comercial/oportunidades',
  'oportunidades-encerradas-v2': '/comercial/oportunidades/encerradas',
  'proposta-details': '/comercial/oportunidades/proposta',
  'proposta-parametro': '/comercial/proposta-parametro',
  'proposta-grupo-imagem-manager': '/comercial/proposta-imagem',
  'oportunidade-status': '/comercial/oportunidades/status',
  // Financeiro
  'bancos': '/financeiro/bancos',
  'contas-bancarias': '/financeiro/contas-bancarias',
  'centros-custo': '/financeiro/centros-custo',
  'categorias-contabeis': '/financeiro/categorias-contabeis',
  'formas-pagamento': '/financeiro/formas-pagamento',
  'contas-receber': '/financeiro/contas-receber',
  'contas-pagar': '/financeiro/contas-pagar',
  'movimentos': '/financeiro/movimentos',
  'caixa': '/financeiro/caixa',
  'comissao': '/comercial/comissao',
  'comissao-parametro': '/comercial/comissao-parametro',
  'comissao-bonus': '/comercial/comissao-bonus',
  'vendas': '/comercial/vendas',
  'proposta-resultado': '/comercial/proposta-resultado',
  'banco-details': '/financeiro/bancos/details',
  'conta-details': '/financeiro/contas-bancarias/details',
  'centro-custo-details': '/financeiro/centros-custo/details',
  // Suprimentos
  'suprimentos-requisicoes-ativas': '/suprimentos/requisicoes',
  'suprimentos-requisicoes-encerradas': '/suprimentos/requisicoes/encerradas',
  'suprimentos-movimentacao': '/suprimentos/movimentacao',
  'suprimentos-balanco': '/suprimentos/balanco',
  'suprimentos-cat': '/suprimentos/cat',
  'suprimentos-triagem': '/suprimentos/triagem',
  'suprimentos-ordem-compra': '/suprimentos/ordem-compra',
  'suprimentos-ordem-compra-status': '/suprimentos/ordem-compra/status',
  'suprimentos-marca': '/suprimentos/marca',
  'suprimentos-produto-categoria': '/suprimentos/produto-categoria',
  'suprimentos-produto-subcategoria': '/suprimentos/produto-subcategoria',
  'suprimentos-produto-tipo': '/suprimentos/produto-tipo',
  'suprimentos-unidade-medida': '/suprimentos/unidade-medida',
  'suprimentos-margem-tipo': '/suprimentos/margem-tipo',
  'suprimentos-classificacao': '/suprimentos/classificacao',
  // Cadastros (flat por opção)
  'pessoas': '/pessoas',
  'empreendimentos': '/empreendimentos',
  'departamentos': '/departamentos',
  'estados': '/estados',
  'cidades': '/cidades',
  'fornecedores': '/fornecedores',
  'produtos': '/produtos',
  'tipos-ambiente': '/tipos-ambiente',
  'tipos-ponto': '/tipos-ponto',
  'grupos-tipo-ponto': '/grupos-tipo-ponto',
  'tipos-servico': '/tipos-servico',
  'tipos-vistoria': '/tipos-vistoria',
  'profissional-tipo': '/profissional-tipo',
  'atraso-motivo': '/atraso-motivo',
  'file-categorias': '/file-categorias',
  'transition-rules': '/transition-rules',
  'obra-etapa': '/obra-etapa',
  'obra-produto-status': '/obra-produto-status',
  'modulos': '/modulos',
  'departamento-details': '/departamentos/details',
  'estado-details': '/estados/details',
  'cidade-details': '/cidades/details',
  'tipo-ambiente-details': '/tipos-ambiente/details',
  'tipo-ponto-details': '/tipos-ponto/details',
  'grupo-tipo-ponto-details': '/grupos-tipo-ponto/details',
  'api-keys': '/api-keys',
  'api-keys-admin': '/admin/api-keys',
  'api-docs': '/api-docs',
  'webhooks': '/webhooks',
  // Admin (flat por opção)
  'equipe': '/usuarios',
  'perfis': '/perfis',
  'auditoria-logs': '/auditoria-logs',
  // Atividade (rebrand → calendario)
  'atividade-consultor': '/calendario',
  'atividade-config-areas': '/calendario/config-areas',
  'atividade-config-tipos': '/calendario/config-tipos',
  // Projetos
  'projetos-ativos': '/projetos',
  'projetos-encerrados': '/projetos/encerrados',
  'projeto-status': '/projetos/status',
  // PDF
  'pdf-template-manager': '/template-pdf',
  'pdf-layout-builder': '/template-pdf/editor',
  // Tools / platform (flat por opção)
  'painel-cargas': '/painel-cargas',
  'orcamento-escopo': '/orcamento-escopo',
  'config': '/config',
  'cable-routing': '/cable-routing',
  'google-chat-manager': '/google-chat-manager',
  'sync-os-fotos': '/sync-os-fotos',
  'auto-services': '/auto-services',
  'produtos-a-comprar': '/produtos-a-comprar',
  'notificacoes': '/notificacoes',
  // Misc
  'tarefas': '/tarefas',
};

const PATH_TO_VIEW: ReadonlyMap<string, View> = new Map(
  (Object.entries(VIEW_TO_PATH) as [View, string][]).map(([view, path]) => [path, view]),
);

export function viewToPath(view: View): string {
  return VIEW_TO_PATH[view] ?? '/';
}

export function pathToView(pathname: string): View | null {
  const normalized = pathname === '' ? '/' : pathname;
  return PATH_TO_VIEW.get(normalized) ?? null;
}
